import React, { FunctionComponent } from 'react';
import styles from './LanguagePicker.module.scss';
import { useContext } from 'react';
import { LanguageContext } from 'context';
import config from 'config/config';
import { LanguageTypes } from 'types/global';
import { useIntl } from 'react-intl';
import messages from 'messages/base-pl';
import { LinkWrapper } from 'components/elements';
import { scrollTop, useRouteTranslations } from 'utils';

interface OwnProps {
  onClose?: () => void;
}

type LanguagePickerProps = JSX.IntrinsicElements['div'] & OwnProps;

const languageMap: Record<LanguageTypes, string> = {
  pl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='512' height='512' viewBox='0 0 512 512'%3E%3Cmask id='a'%3E%3Ccircle cx='256' cy='256' r='256' fill='%23fff'/%3E%3C/mask%3E%3Cg mask='url(%23a)'%3E%3Cpath fill='%23d80027' d='m0 256 256.4-44.3L512 256v256H0z'/%3E%3Cpath fill='%23eee' d='M0 0h512v256H0z'/%3E%3C/g%3E%3C/svg%3E",
  en: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='512' height='512' viewBox='0 0 512 512'%3E%3Cmask id='a'%3E%3Ccircle cx='256' cy='256' r='256' fill='%23fff'/%3E%3C/mask%3E%3Cg mask='url(%23a)'%3E%3Cpath fill='%23eee' d='M0 0h47.4l76.4 21 65.4-21h33.4l34.2 16.6L289.4 0h33.4l70.4 22.8L464.8 0h15.8l12.2 7.3L512 0v47.3l-19.9 78 19.9 63.9v33.4l-16.4 30.6 16.4 36.2v33.4l-15.1 68.7 15.1 73.3v15.9l-7.8 10.9L512 512h-47.3l-71-17.5-70.9 17.5h-33.4l-30-19.7-36.8 19.7h-33.3l-63.7-20.2L47.3 512H31.4l-10.6-8L0 512v-47.3l22.8-79L0 322.9v-33.4l25.3-32L0 222.6v-33.4l22.2-64.6L0 47.2V31.4l8-7.8z'/%3E%3Cpath fill='%230052b4' d='m47.4 0 141.8 142V0H47.4zm275.4 0v142l142-142h-142zM0 47.2v142h142L0 47.2zm512 .1L370.1 189.2H512V47.3zM0 322.8v141.9l141.9-141.9H0zm370 0 142 142v-142H370zM189.3 370l-142 142h142V370zm133.5.1V512h141.9L322.8 370.1z'/%3E%3Cpath fill='%23d80027' d='M222.6 0v222.6H0v66.8h222.6V512h66.8V289.4H512v-66.8H289.4V0h-66.8z'/%3E%3Cpath fill='%23d80027' d='M0 0v31.4l157.7 157.8h31.5L0 0zm480.6 0L322.8 157.7v31.5L512 0h-31.4zM189.2 322.8 0 512h31.4l157.8-157.7v-31.5zm133.6 0L511.9 512h.1v-31.3L354.3 322.8h-31.5z'/%3E%3C/g%3E%3C/svg%3E",
  de: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='512' height='512'%3E%3Cmask id='germany'%3E%3Ccircle cx='256' cy='256' r='256' fill='%23fff'/%3E%3C/mask%3E%3Cg mask='url(%23germany)'%3E%3Cpath fill='%23ffda44' d='m0 345 257-25 255 25v167H0z'/%3E%3Cpath fill='%23d80027' d='m0 167 255-23 257 23v178H0z'/%3E%3Cpath fill='%23333' d='M0 0h512v167H0z'/%3E%3C/g%3E%3C/svg%3E",
};

const LanguagePicker: FunctionComponent<LanguagePickerProps> = props => {
  const { language: selectedLanguage, setLoading } = useContext(LanguageContext);
  const { languages } = config;
  const { formatMessage } = useIntl();
  const { translatePath } = useRouteTranslations();

  console.log('selected', selectedLanguage);

  const changeLanguage = (languageCode: LanguageTypes) => (event: any) => {
    event.preventDefault();
    setLoading(true);
    scrollTop(() => {
      window.location.href = translatePath(languageCode);
    });
  };

  return (
    <div className={styles.item}>
      {languages
        .filter(language => language !== selectedLanguage)
        .filter(language => language !== 'de')
        .map(language => (
          <LinkWrapper
            key={`lang-${language}`}
            href="#"
            onClick={changeLanguage(language)}
            aria-label={`${formatMessage(messages.language)} - ${language}`}
            className={styles.link}
          >
            {/* @ts-ignore */}
            <img className={styles.icon} alt={`${language} flag`} src={languageMap[language]} />
          </LinkWrapper>
        ))}
      <LinkWrapper
        target="_blank"
        rel="noopener noreferrer"
        href="https://testarmy.fr"
        aria-label={`${formatMessage(messages.language)} - fr`}
        className={styles.link}
      >
        <img
          className={styles.icon}
          alt={`fr flag`}
          src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xml:space='preserve' viewBox='0 0 512 512'%3E%3Ccircle cx='256' cy='256' r='256' fill='%23f0f0f0'/%3E%3Cpath fill='%23d80027' d='M512 256c0-110-69-204-167-240v480c98-36 167-130 167-240z'/%3E%3Cpath fill='%230052b4' d='M0 256c0 110 69 204 167 240V16A256 256 0 0 0 0 256z'/%3E%3C/svg%3E"
        />
      </LinkWrapper>
      <LinkWrapper
        target="_blank"
        rel="noopener noreferrer"
        href="https://testarmy.de"
        aria-label={`${formatMessage(messages.language)} - de`}
        className={styles.link}
      >
        <img
          className={styles.icon}
          alt={`de flag`}
          src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' shape-rendering='geometricPrecision' text-rendering='geometricPrecision' image-rendering='optimizeQuality' fill-rule='evenodd' clip-rule='evenodd' viewBox='0 0 512 512'%3E%3Cg fill-rule='nonzero'%3E%3Cpath d='M255.99 39.59c119.51 0 216.4 96.89 216.4 216.4 0 119.52-96.89 216.42-216.4 216.42-119.52 0-216.41-96.9-216.41-216.42 0-119.51 96.89-216.4 216.41-216.4z'/%3E%3Cpath fill='%23D00' d='M459.56 182.47H52.41c-8.3 22.96-12.83 47.71-12.83 73.52 0 119.52 96.89 216.42 216.41 216.42 119.51 0 216.4-96.9 216.4-216.42 0-25.81-4.53-50.56-12.83-73.52z'/%3E%3Cpath fill='%23FFCE00' d='M459.56 329.55H52.41c30.11 83.32 109.89 142.86 203.58 142.86 93.69 0 173.47-59.54 203.57-142.86z'/%3E%3C/g%3E%3C/svg%3E"
        />
      </LinkWrapper>
    </div>
  );
};

export default LanguagePicker;
